import { keepPreviousData } from '@tanstack/react-query';
import {
    RoleStatusEnum,
    SearchTypeEnum,
    StringFilterType,
    useRepositoryInfiniteQuery,
    useRepositoryMutation,
    useRepositoryQuery,
    UserProfileFilters,
    UserProfileResult,
    UserProfileSearchResult,
} from '@mews-commander/data-access';
import { TableState } from '@optimus-web/core';
import { showSuccessAlert, LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';

export const useRoleUsersData = (tableState: TableState<UserProfileResult, UserProfileFilters>, roleId: string) => {
    const { filters, sort, pagination, globalFilters } = tableState;

    return useRepositoryQuery(
        'userProfiles',
        'getAll',
        {
            filters,
            sort,
            pagination,
            roleIds: [roleId],
            ...(globalFilters.search && {
                globalSearchFilter: { value: globalFilters.search, filterType: StringFilterType.Contains },
            }),
        },
        {
            placeholderData: keepPreviousData,
        }
    );
};

export const useAddRoleUsersData = (tableState: TableState<UserProfileSearchResult>, roleId: string) => {
    const PAGE_SIZE = 10;

    return useRepositoryInfiniteQuery(
        'userProfiles',
        'search',
        ({ pageParam }) => ({
            pagination: { pageSize: PAGE_SIZE, pageIndex: pageParam },
            ...(tableState.globalFilters.search && {
                globalSearchFilter: {
                    value: tableState.globalFilters.search,
                    filterType: StringFilterType.Contains,
                },
            }),
            roleStatus: { id: roleId, status: RoleStatusEnum.Unassigned },
            type: SearchTypeEnum.Simple,
            excludeProvisionedUsers: true,
        }),
        {
            placeholderData: keepPreviousData,
            initialPageParam: 0,
            getNextPageParam: (lastPage, allPages) =>
                lastPage.totalRowCount < PAGE_SIZE * allPages.length ? undefined : allPages.length,
        }
    );
};

export const useRemoveRoleUsers = (roleName: string) =>
    useRepositoryMutation('roles', 'assignUsers', {
        transformParams: (params) => ({ ...params, unassign: true }),
        onSuccess: (_, { userIds }) => {
            showSuccessAlert({
                description: (
                    <LocalizedText
                        textKey={TextKey.RoleEditRemoveUsersSuccess}
                        substitutes={{ roleName, count: userIds.length }}
                    />
                ),
            });
        },
        invalidateQueries: {
            roles: ['getAll'],
            userProfiles: ['getAll', 'search', 'get'],
        },
    });

export const useAddRoleUsers = (roleName: string) =>
    useRepositoryMutation('roles', 'assignUsers', {
        transformParams: (params) => ({ ...params, unassign: false }),
        onSuccess: (_, { userIds }) => {
            showSuccessAlert({
                description: (
                    <LocalizedText
                        textKey={TextKey.RoleEditAddUsersSuccess}
                        substitutes={{ roleName, count: userIds.length }}
                    />
                ),
            });
        },
        invalidateQueries: {
            roles: ['getAll'],
            userProfiles: ['getAll', 'search', 'get'],
        },
    });
